import { Subscribable, SubscribableOrNullableValue } from "knockout";
import { withEffects } from "mixins/withEffects";
import { WCCSliderConfig } from "../config";
import WCCSliderItem from "../item/base/base.model";
import WCCSliderPage from "../models/page";
import { IWCCSliderLayout } from "./layout.interfaces";

export interface WCCSliderBaseLayoutConfig<T, I extends WCCSliderItem<T>> {
    source: SubscribableOrNullableValue<T[]>

    width: SubscribableOrNullableValue<number>
    otherPageElementWidth: SubscribableOrNullableValue<number>

    activePage: SubscribableOrNullableValue<WCCSliderPage<T, I>>

    showOtherPages: SubscribableOrNullableValue<boolean>
}

export interface WCCSliderLayoutPageWidthCalculationConfig {
    isFirst: boolean
    isLast: boolean
}

export default abstract class WCCSliderBaseLayout<T, I extends WCCSliderItem<T>, C> implements IWCCSliderLayout<T, I> {
    protected effects = withEffects();

    protected width: Subscribable<number>
    protected otherPageElementWidth: Subscribable<number>
    protected areOtherPagesShown: Subscribable<boolean>

    protected activePage: Subscribable<WCCSliderPage<T, I> | undefined>

    items: Subscribable<I[]>
    pages: Subscribable<WCCSliderPage<T, I>[]>

    height: Subscribable<number | undefined>
    spaceBetweenPages: Subscribable<number>
    abstract itemWidth: Subscribable<number>

    constructor(config: WCCSliderBaseLayoutConfig<T, I>) {
        this.activePage = ko.flattenComputed(config.activePage);

        const source = ko.flattenComputed(config.source, []);

        this.width = ko.flattenComputed(config.width, 0);
        this.otherPageElementWidth = ko.flattenComputed(config.otherPageElementWidth, 0);
        this.areOtherPagesShown = ko.flattenComputed(config.showOtherPages, false);

        this.items = source
            .mapSingle(source => this.width() > 0 ? source : [])
            .map(data => this.buildItem(data), this);

        this.height = ko.pureComputed(() => this.getHeight());

        this.pages = this.items.mapSingle(items => this.buildPages(items)).extend({ deferred: true });
        this.spaceBetweenPages = ko.pureComputed(() => this.getSpaceBetweenPages());

        queueMicrotask(() => {
            this.effects.register([
                ko.computed(() => this.updateBorders(), this).extend({ deferred: true })
            ]);
        })
    }

    dispose() {
        this.effects.dispose();
    }

    protected abstract buildItem(data: T): I
    protected abstract getContext(pages: WCCSliderPage<T, I>[], page: WCCSliderPage<T, I>, isFinal?: boolean): C
    protected abstract canAddItem(item: I, context: C): boolean

    protected getPageWidth(config: WCCSliderLayoutPageWidthCalculationConfig) {
        if (config.isFirst && config.isLast || !this.areOtherPagesShown()) {
            return this.width();
        } else {
            if (config.isFirst || config.isLast) {
                return this.width() - this.otherPageElementWidth();
            } else {
                return this.width() - this.otherPageElementWidth() * 2;
            }
        }
    }     

    protected getSpaceBetweenPages(): number {
        return WCCSliderConfig.spaceBetweenPages;
    }

    protected getHeight(): number | undefined {
        return undefined;
    }

    private buildPages(items: I[]) {
        const pages = new Array<WCCSliderPage<T, I>>();
        const itemsToAdd = [...items];

        while (itemsToAdd.length > 0) {
            const page = new WCCSliderPage<T, I>();
            const context = this.getContext(pages.length > 0 ? pages : [page], page);

            const canAdd = () => itemsToAdd.length > 0 && this.canAddItem(itemsToAdd[0], context);

            while (ko.ignoreDependencies(canAdd)) {
                const item = <I>itemsToAdd.shift();
                item.width(); //read width to force reevaluation on change

                page.items.push(item);
            }

            if (page.items().length > 0) {
                pages.push(page);
            } else {
                break;
            }
        }

        ko.ignoreDependencies(() => {
            if (pages.length > 1)
                this.fillLastPage(pages[pages.length - 1], pages[pages.length - 2], pages);
        });

        return pages;
    }

    private fillLastPage(page: WCCSliderPage<T, I>, sourcePage: WCCSliderPage<T, I>, pages: WCCSliderPage<T, I>[]) {
        const context = this.getContext(pages, page, true);

        const availableItems = [...sourcePage.items()].reverse();

        for (const item of availableItems) {
            const canAdd = this.canAddItem(item, context);

            if (canAdd)
                page.borrowedItems.unshift(item);
            else
                break;            
        }
    }    

    private updateBorders() {
        if (!this.areOtherPagesShown()) {
            this.pages().forEach((page, pageIdx) => {
                const items = page === this.activePage() ? page.allItems() : page.items();

                items.forEach((item, idx) => { item.marginLeft(pageIdx > 0 && idx === 0 ? this.spaceBetweenPages() : 0) });
            }, this);
        }
    }    
}